import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { useErrorReporter } from "@/components/error-reporter";
import { ShieldAlert, Trash2, RefreshCw, Gauge } from "lucide-react";
import { SuperAdminTopBar } from "@/components/superadmin/top-bar";
import { StatCards } from "@/components/superadmin/stat-cards";
import { initialsOf } from "@/components/charts/bits";
import {
  QuestionMixCard,
  StatusDonutCard,
  AttemptsTrendCard,
} from "@/components/superadmin/chart-cards";
import {
  listAllUsers,
  setUserDisabled,
  setUserRole,
  deleteUser,
  superadminDashboard,
} from "@/lib/superadmin.functions";

export const Route = createFileRoute("/_authenticated/superadmin")({
  head: () => ({ meta: [{ title: "Superadmin — Subiza" }] }),
  beforeLoad: async ({ context }) => {
    // Tightened route gate: the parent layout already proved the user is signed
    // in; here we verify the superadmin role before anything renders, so a
    // non-superadmin never sees a frame of this page. The authoritative check
    // still happens server-side inside every server function (assertSuperadmin).
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.user.id);
    if (!roles?.some((r) => r.role === "superadmin")) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: SuperadminPage,
});

function SuperadminPage() {
  const { user, roles, loading } = useAuth();
  const isSuper = roles.includes("superadmin");
  const dashboardCall = useServerFn(superadminDashboard);
  const list = useServerFn(listAllUsers);
  const setDisabled = useServerFn(setUserDisabled);
  const setRole = useServerFn(setUserRole);
  const del = useServerFn(deleteUser);
  const { reportError } = useErrorReporter();

  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState(null);
  const [dash, setDash] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [dashError, setDashError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  async function loadDashboard() {
    try {
      setDash(await dashboardCall());
      setDashError(null);
    } catch (e) {
      setDashError(e);
      reportError(e, { area: "superadmin.dashboard", retry: loadDashboard });
    } finally {
      setDashLoading(false);
    }
  }

  async function loadUsers() {
    try {
      setRows(await list());
      setRowsError(null);
    } catch (e) {
      setRowsError(e);
      reportError(e, { area: "superadmin.list", retry: loadUsers });
    } finally {
      setRowsLoading(false);
    }
  }

  async function refresh() {
    setRefreshing(true);
    await Promise.allSettled([loadDashboard(), loadUsers()]);
    setRefreshing(false);
  }

  useEffect(() => {
    if (!loading && isSuper) {
      loadDashboard();
      loadUsers();
    }
  }, [loading, isSuper]);

  if (loading) return <div className="min-h-screen bg-bg" aria-busy="true" />;

  if (!isSuper) {
    return (
      <div className="min-h-screen bg-bg">
        <div className="container mx-auto px-4 py-16 text-center">
          <ShieldAlert className="mx-auto size-12 text-destructive" />
          <h1 className="mt-4 text-3xl font-bold">Superadmin only</h1>
          <p className="text-muted mt-2">You need superadmin privileges to view this page.</p>
          <Button
            asChild
            className="mt-6 bg-gradient-primary text-accent-foreground shadow-elegant"
          >
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  async function toggleDisabled(r) {
    if (r.id === user?.id) return toast.error("You can't disable yourself.");
    setBusy(true);
    try {
      await setDisabled({ data: { userId: r.id, disabled: !r.disabled } });
      toast.success(!r.disabled ? "User disabled" : "User enabled");
      await loadUsers();
    } catch (e) {
      reportError(e, { area: "superadmin.setDisabled" });
    } finally {
      setBusy(false);
    }
  }

  async function toggleRole(r, role) {
    const has = r.roles.includes(role);
    setBusy(true);
    try {
      await setRole({ data: { userId: r.id, role, grant: !has } });
      toast.success(`${has ? "Removed" : "Granted"} ${role}`);
      await loadUsers();
    } catch (e) {
      reportError(e, { area: "superadmin.setRole" });
    } finally {
      setBusy(false);
    }
  }

  async function removeUser(r) {
    if (!confirm(`Permanently delete ${r.email}? This deletes their quizzes too.`)) return;
    setBusy(true);
    try {
      await del({ data: { userId: r.id } });
      toast.success("User deleted");
      await loadUsers();
    } catch (e) {
      reportError(e, { area: "superadmin.deleteUser" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg">
      <div className="container mx-auto max-w-7xl px-4 pb-12">
        <SuperAdminTopBar
          isAdmin={roles.includes("admin") || isSuper}
          isSuperAdmin={isSuper}
          refresh={refresh}
          refreshing={refreshing}
        />

        <main className="mt-8 space-y-8">
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted">Overview</p>
              <h1 className="mt-1 text-3xl font-light tracking-tight sm:text-4xl">
                Platform analytics
              </h1>
              <p className="mt-1 text-sm text-muted">
                Real-time aggregates over every teacher, exam and student attempt.
              </p>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/quota">
                <Gauge className="size-4" /> AI quota
              </Link>
            </Button>
          </header>

          {dashError ? (
            <DashboardError error={dashError} onRetry={() => loadDashboard()} />
          ) : dashLoading || !dash ? (
            <DashboardSkeleton />
          ) : (
            <>
              <StatCards stats={dash.stats} />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <QuestionMixCard data={dash.questionMix} />
                <StatusDonutCard data={dash.statusDonut} />
                <AttemptsTrendCard data={dash.attempts} />
              </div>
            </>
          )}

          <section id="users" className="scroll-mt-24">
            <Card className="overflow-hidden rounded-3xl border border-border bg-surface shadow-soft">
              <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
                <h2 className="font-display text-lg font-semibold tracking-tight">
                  All users <span className="text-sm font-normal text-muted">({rows.length})</span>
                </h2>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => loadUsers()}
                  disabled={rowsLoading}
                >
                  <RefreshCw className={`size-4 ${rowsLoading ? "animate-spin" : ""}`} />
                  {rowsLoading ? "Reloading" : "Refresh"}
                </Button>
              </div>

              {rowsError ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-muted">
                    Couldn't load the user list.{" "}
                    <button
                      className="font-medium text-accent underline underline-offset-4"
                      onClick={() => loadUsers()}
                    >
                      Try again
                    </button>
                  </p>
                </div>
              ) : rowsLoading ? (
                <div className="space-y-3 p-5">
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-surface-2/40">
                      <tr className="text-left">
                        <th className="p-3 font-medium text-muted">User</th>
                        <th className="p-3 font-medium text-muted">Roles</th>
                        <th className="p-3 font-medium text-muted">Activity</th>
                        <th className="p-3 font-medium text-muted">Status</th>
                        <th className="p-3 text-right font-medium text-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id} className="border-t border-border">
                          <td className="p-3">
                            <div className="flex items-center gap-2 font-medium">
                              <span className="grid size-7 place-items-center rounded-full bg-accent/10 text-xs font-semibold text-accent">
                                {initialsOf(r.name)}
                              </span>
                              <span>
                                {r.name}
                                {r.id === user?.id && (
                                  <span className="text-xs text-muted ml-2">(you)</span>
                                )}
                              </span>
                            </div>
                            <div className="pl-9 text-xs text-muted">{r.email}</div>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1">
                              {["superadmin", "admin", "teacher"].map((role) => {
                                const has = r.roles.includes(role);
                                return (
                                  <button
                                    key={role}
                                    onClick={() => toggleRole(r, role)}
                                    disabled={busy}
                                    className="focus:outline-none"
                                    title={has ? `Remove ${role}` : `Grant ${role}`}
                                  >
                                    <Badge
                                      variant={has ? "default" : "outline"}
                                      className="cursor-pointer"
                                    >
                                      {role}
                                    </Badge>
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                          <td className="p-3 text-muted">
                            {r.quizCount} quizzes · {r.sessionCount} games
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={!r.disabled}
                                disabled={busy || r.id === user?.id}
                                onCheckedChange={() => toggleDisabled(r)}
                              />
                              <span className={r.disabled ? "text-destructive" : "text-success"}>
                                {r.disabled ? "Disabled" : "Active"}
                              </span>
                            </div>
                          </td>
                          <td className="p-3 text-right">
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={busy || r.id === user?.id}
                              onClick={() => removeUser(r)}
                            >
                              <Trash2 className="size-4" /> Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={5} className="p-6 text-center text-muted">
                            No users yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <p className="mt-3 text-xs text-muted">
              Tip: click a role badge to grant or revoke it. Disabling a user signs them out and
              blocks new quiz creation. Only superadmins can view the analytics above; the data is
              served by a role-checked server function.
            </p>
          </section>
        </main>
      </div>
    </div>
  );
}

function DashboardError({ error, onRetry }) {
  return (
    <div className="grid place-items-center rounded-3xl border border-dashed border-border bg-surface p-10">
      <div className="text-center">
        <ShieldAlert className="mx-auto size-10 text-destructive" />
        <p className="mt-3 text-sm font-medium text-text">Couldn't load platform analytics</p>
        <p className="mt-1 text-sm text-muted">
          {error?.userMessage || "Something went wrong while gathering the aggregates."}
        </p>
        <Button className="mt-4" variant="outline" onClick={onRetry}>
          <RefreshCw className="size-4" /> Try again
        </Button>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  const card = "rounded-3xl border border-border bg-surface p-5 shadow-soft";
  return (
    <div className="space-y-8" aria-busy="true">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className={card}>
            <Skeleton className="size-10 rounded-full" />
            <Skeleton className="mt-4 h-9 w-24" />
            <Skeleton className="mt-3 h-4 w-32" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className={card}>
            <Skeleton className="h-5 w-28" />
            <Skeleton className="mt-4 h-36 w-full" />
            <Skeleton className="mt-4 h-3 w-2/3" />
          </div>
        ))}
      </div>
    </div>
  );
}
